/* ============================================================
   JERKIN' IT v2.0 — HERO
   Flavour browser. Keyboard-operable, swipeable, deep-linkable.
   ============================================================ */
(() => {
  'use strict';

  class JIProductHero extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-hero-slide]')];
      this.tabs = [...this.querySelectorAll('[data-hero-tab]')];
      if (!this.slides.length) return;

      this.index = 0;
      this.busy = false;
      this.qty = 1;

      this.el = {
        number: this.querySelector('[data-hero-number]'),
        total: this.querySelector('[data-hero-total]'),
        line1: this.querySelector('[data-hero-line1]'),
        line2: this.querySelector('[data-hero-line2]'),
        tagline: this.querySelector('[data-hero-tagline]'),
        stars: this.querySelector('[data-hero-stars]'),
        rating: this.querySelector('[data-hero-rating]'),
        specs: this.querySelector('[data-hero-specs]'),
        price: this.querySelector('[data-hero-price]'),
        unit: this.querySelector('[data-hero-unit]'),
        qty: this.querySelector('[data-hero-qty]'),
        cta: this.querySelector('[data-hero-add]'),
        ctaText: this.querySelector('[data-hero-add-text]'),
        status: this.querySelector('[data-hero-status]'),
        dragHint: this.querySelector('[data-hero-drag-hint]')
      };

      if (this.el.total) this.el.total.textContent = String(this.slides.length).padStart(2, '0');
      // Only hint at dragging when there is somewhere to drag to.
      if (this.el.dragHint) this.el.dragHint.hidden = this.slides.length < 2;

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.setSlide(i, true));
        tab.addEventListener('keydown', (e) => this.onTabKey(e, i));
      });

      this.querySelector('[data-qty-minus]')?.addEventListener('click', () => this.setQty(this.qty - 1));
      this.querySelector('[data-qty-plus]')?.addEventListener('click', () => this.setQty(this.qty + 1));
      this.el.cta?.addEventListener('click', () => this.add());

      if (this.slides.length > 1) this.bindSwipe();

      // Deep link: /?flavour=mighty-cheese
      const params = new URLSearchParams(location.search);
      const key = window.JI?.landingProductParameter || 'flavour';
      const requested = params.get(key) || params.get('product');
      const found = requested ? this.slides.findIndex(s => s.dataset.productHandle === requested) : -1;
      this.render(found >= 0 ? found : 0, false);
    }

    data(index) {
      const el = this.slides[index];
      let specs = [];
      try { specs = JSON.parse(el.dataset.specs || '[]'); } catch (e) { specs = []; }
      return {
        accent: el.dataset.accent || '',
        title1: el.dataset.title1 || '',
        title2: el.dataset.title2 || '',
        tagline: el.dataset.tagline || '',
        rating: el.dataset.rating || '',
        ratingLabel: el.dataset.ratingLabel || '',
        starFill: parseFloat(el.dataset.starFill || '0'),
        specs,
        price: el.dataset.price || '',
        unit: el.dataset.unit || '',
        variant: el.dataset.variant || '',
        available: el.dataset.available === 'true',
        title: el.dataset.productTitle || '',
        handle: el.dataset.productHandle || ''
      };
    }

    render(index, animate = true) {
      const d = this.data(index);
      this.index = index;

      // The entire hero environment is repainted from one custom property.
      if (d.accent) this.style.setProperty('--ji-flavour', d.accent);

      this.slides.forEach((s, i) => {
        s.classList.toggle('is-active', i === index);
        s.style.removeProperty('--ji-drag');
      });
      this.tabs.forEach((t, i) => {
        const on = i === index;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-pressed', String(on));
        t.tabIndex = on ? 0 : -1;
      });

      if (this.el.number) this.el.number.textContent = String(index + 1).padStart(2, '0');
      if (this.el.line1) this.el.line1.textContent = d.title1;
      if (this.el.line2) this.el.line2.textContent = d.title2;
      if (this.el.tagline) this.el.tagline.textContent = d.tagline;
      if (this.el.rating) this.el.rating.textContent = d.rating;
      this.setStars(d);

      if (this.el.specs) {
        this.el.specs.innerHTML = d.specs
          .filter(Boolean)
          .map(s => `<span class="ji-hero__spec">${window.JI.escapeHtml(s)}</span>`)
          .join('');
      }
      if (this.el.price) this.el.price.textContent = d.price;
      if (this.el.unit) this.el.unit.textContent = d.unit;
      if (this.el.cta) this.el.cta.disabled = !d.available || !d.variant;
      if (this.el.ctaText) {
        this.el.ctaText.textContent = d.available
          ? 'Add to bag'
          : (window.JI?.soldOutLabel || 'You lot ate it all');
      }
      if (this.el.status) this.el.status.textContent = '';
      this.setQty(1);

      if (animate && !window.JI.reduceMotion() && this.el.line2) {
        this.el.line2.animate(
          [{ opacity: .25, transform: 'translateY(9px)' }, { opacity: 1, transform: 'none' }],
          { duration: 440, easing: 'cubic-bezier(.16,1,.3,1)' }
        );
      }
      if (animate) {
        window.JI.track('hero_flavour_changed', { handle: d.handle, title: d.title, index: index + 1 });
        // Keep the URL shareable without adding history entries.
        const key = window.JI?.landingProductParameter || 'flavour';
        const url = new URL(location.href);
        url.searchParams.set(key, d.handle);
        history.replaceState({}, '', url);
      }
    }

    setStars(d) {
      const stars = this.el.stars;
      if (!stars) return;
      const pct = Number.isFinite(d.starFill) ? Math.max(0, Math.min(100, d.starFill)) : 0;
      if (pct <= 0) {
        stars.hidden = true;
        stars.removeAttribute('role');
        stars.removeAttribute('aria-label');
        return;
      }
      stars.hidden = false;
      stars.style.setProperty('--ji-star-fill', pct + '%');
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', d.ratingLabel);
    }

    setSlide(index, focusTab = false) {
      if (index < 0) index = this.slides.length - 1;
      if (index >= this.slides.length) index = 0;
      if (index === this.index) return;
      this.render(index, true);
      if (focusTab) this.tabs[index]?.focus();
    }

    onTabKey(e, i) {
      const keys = { ArrowLeft: i - 1, ArrowRight: i + 1, Home: 0, End: this.tabs.length - 1 };
      if (!(e.key in keys)) return;
      e.preventDefault();
      let next = keys[e.key];
      if (next < 0) next = this.tabs.length - 1;
      if (next >= this.tabs.length) next = 0;
      this.setSlide(next, true);
    }

    setQty(value) {
      this.qty = Math.max(1, Math.min(99, value));
      if (this.el.qty) {
        this.el.qty.value = this.qty;
        this.el.qty.textContent = this.qty;
      }
      // Easter egg: acknowledge ambition, never block it.
      if (this.el.status && this.qty >= 12) this.el.status.textContent = 'Planning something?';
      else if (this.el.status && this.el.status.textContent === 'Planning something?') this.el.status.textContent = '';
    }

    /* --- Swipe / drag, with pointer capture so gestures cannot be lost --- */
    bindSwipe() {
      let startX = null, startY = null, active = false, pointerId = null, axis = null;
      const stage = this.querySelector('.ji-hero__stage') || this;

      const down = (e) => {
        // Ignore gestures that begin on a control.
        if (e.target.closest('button,a,input,select,output')) return;
        startX = e.clientX;
        startY = e.clientY;
        active = true;
        axis = null;
        pointerId = e.pointerId;
        try { stage.setPointerCapture(pointerId); } catch (err) { /* not capturable */ }
      };
      const move = (e) => {
        if (!active || startX === null) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        if (!axis && Math.max(Math.abs(dx), Math.abs(dy)) >= 8) {
          axis = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
        }
        if (axis !== 'horizontal') return;
        const current = this.slides[this.index];
        if (current && !window.JI.reduceMotion()) {
          // Rubber-band at the ends rather than sliding into nothing.
          const atEdge = (dx > 0 && this.index === 0) || (dx < 0 && this.index === this.slides.length - 1);
          current.style.setProperty('--ji-drag', (atEdge ? dx * 0.25 : dx * 0.6) + 'px');
        }
      };
      const end = (e) => {
        if (!active || startX === null) return;
        const dx = e.clientX - startX;
        const wasHorizontal = axis === 'horizontal';
        active = false;
        startX = null;
        startY = null;
        axis = null;
        try { stage.releasePointerCapture(pointerId); } catch (err) { /* ignore */ }
        this.slides[this.index]?.style.removeProperty('--ji-drag');
        if (!wasHorizontal || Math.abs(dx) < 50) return;
        this.setSlide(this.index + (dx < 0 ? 1 : -1));
      };
      const cancel = () => {
        active = false;
        startX = null;
        startY = null;
        axis = null;
        this.slides[this.index]?.style.removeProperty('--ji-drag');
      };

      stage.addEventListener('pointerdown', down);
      stage.addEventListener('pointermove', move);
      stage.addEventListener('pointerup', end);
      stage.addEventListener('pointercancel', cancel);
      stage.addEventListener('lostpointercapture', cancel);
    }

    async add() {
      const d = this.data(this.index);
      if (!d.variant || !d.available || this.busy) return;
      this.busy = true;
      this.el.cta.disabled = true;
      this.el.ctaText.textContent = 'Adding…';
      try {
        await window.JIStore.addItems([{ id: Number(d.variant), quantity: this.qty }]);
        window.JI.track('quick_add', { source: 'hero', handle: d.handle, quantity: this.qty });
        this.el.ctaText.textContent = 'Added';
        this.el.status.textContent = this.qty > 1 ? `${this.qty} bags in the bag.` : 'In the bag.';
        setTimeout(() => { this.el.ctaText.textContent = 'Add to bag'; }, 1200);
      } catch (err) {
        this.el.status.textContent = err.message || 'Something went wrong.';
        this.el.ctaText.textContent = 'Try again';
      } finally {
        this.busy = false;
        this.el.cta.disabled = !d.available;
      }
    }
  }

  if (!customElements.get('ji-product-hero')) customElements.define('ji-product-hero', JIProductHero);
})();
